import { Fragment } from "react";
import { ArrowDown, ArrowUp, Check, Sparkles } from "lucide-react";
import type { Round3Verse } from "@versedrill/core";
import { optionState, type Level } from "@/lib/game.ts";
import { cn } from "@/lib/utils";
import { RedactedText } from "./Redacted.tsx";

/** Round 3: the neighbouring verses of the section, with a slot between each where the mystery verse could sit. */
export function SlotList({
  level,
  verses,
  redactionOn,
  foundText,
  disabled,
  onPick,
}: {
  level: Level;
  verses: Round3Verse[];
  redactionOn: boolean;
  /** The mystery passage as shown at the top, revealed in its slot once found. */
  foundText: string;
  disabled: boolean;
  onPick: (i: number) => void;
}) {
  const solved = level.correct !== null;
  const slotCount = verses.length + 1;

  const slot = (i: number) => {
    const st = optionState(level, i);
    const dir = level.wrong[i];
    const inert = solved || st === "wrong" || disabled;
    if (st === "correct") {
      return (
        <div className="animate-found rounded-lg border-2 border-found bg-found/10 px-4 py-3">
          <div className="mb-1 flex items-center gap-1.5 font-ui text-xs font-semibold uppercase tracking-widest text-found">
            <Check className="size-3.5" aria-hidden /> Your verse · +{level.earned}
          </div>
          <p className="font-body text-lg italic leading-relaxed">
            <RedactedText text={foundText} />
          </p>
        </div>
      );
    }
    return (
      <button
        type="button"
        disabled={inert}
        onClick={() => onPick(i)}
        aria-label={`Place the verse in position ${i + 1} of ${slotCount}`}
        className={cn(
          "flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-2.5 font-ui text-sm font-medium transition-colors",
          st === "idle" && !inert && "border-border text-muted-foreground hover:border-brand hover:bg-secondary/70 hover:text-foreground",
          st === "idle" && inert && "border-border/50 text-muted-foreground/50",
          st === "wrong" && "border-wrong/40 bg-wrong/10 text-muted-foreground",
          st === "hinted" && "border-hint bg-hint/15 text-foreground",
        )}
      >
        {st === "wrong" ? (
          <>
            {dir === "below" ? <ArrowDown className="size-4" aria-hidden /> : <ArrowUp className="size-4" aria-hidden />}
            Look {dir === "below" ? "below" : "above"}
          </>
        ) : st === "hinted" ? (
          <>
            <Sparkles className="size-4" aria-hidden /> The verse could be here
          </>
        ) : (
          "The verse is here"
        )}
      </button>
    );
  };

  return (
    <div className="animate-leaf-in flex flex-col gap-3 rounded-xl border border-border bg-card p-3 sm:p-4">
      {Array.from({ length: slotCount }, (_, i) => (
        <Fragment key={i}>
          {slot(i)}
          {verses[i] && (
            <p className="px-2 font-body text-[1.02rem] leading-relaxed text-foreground/80">
              <RedactedText text={redactionOn ? verses[i]!.redacted : verses[i]!.text} />
            </p>
          )}
        </Fragment>
      ))}
    </div>
  );
}
